import * as React from "react";
import { Grid, TextField, Alert, Box, Button, Paper, CircularProgress, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import DownArrow from "../DownArrow";
import DialplanNode, { useDialplanNodeContext } from "./DialplanNode";
import { useDialplanEditorContext } from "./DialplanEditorContext";
import TtsAnnounce from "./TtsAnnounce";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BASE_API_URL } from "../../Config";
import axios from "axios";
import useAuthentication from "../../service/AuthenticationService";
import Hangup from "./HangupNode";

const instance = axios.create({
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  }
});

const MAX_ATTACHMENT_SIZE_MB = 5;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

// WhatsApp logo as SVG component
const WhatsAppLogo = ({ size = 24, color = "#25D366" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

function useWhatsappStatus(phoneUuid) {
  const authentication = useAuthentication();
  const targetPhoneUuid = phoneUuid || authentication.phoneUuid;

  return useQuery({
    queryKey: ['whatsapp-status', targetPhoneUuid],
    queryFn: async () => {
      const response = await instance.get(
        `${BASE_API_URL}/phones/${targetPhoneUuid}/whatsapp/status`,
        {
          headers: {
            "Authorization": "Bearer " + authentication.token
          }
        }
      );
      return response.data;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 10_000,
    enabled: !!targetPhoneUuid && !!authentication.token,
  });
}

function useConnectWhatsapp(phoneUuid) {
  const authentication = useAuthentication();
  const queryClient = useQueryClient();
  const targetPhoneUuid = phoneUuid || authentication.phoneUuid;

  return useMutation({
    mutationFn: async () => {
      const response = await instance.post(
        `${BASE_API_URL}/phones/${targetPhoneUuid}/whatsapp/connect`,
        {},
        {
          headers: {
            "Authorization": "Bearer " + authentication.token
          }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp-status', targetPhoneUuid]);
    },
  });
}

function useAttachmentUpload() {
  const authentication = useAuthentication();

  return useMutation({
    mutationFn: async (file) => {
      const presignedResponse = await instance.post(
        `${BASE_API_URL}/whatsapp/attachment-upload`,
        {},
        {
          headers: {
            "Authorization": "Bearer " + authentication.token
          }
        }
      );

      const { uploadUrl, fields, fileKey } = presignedResponse.data;

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { fileKey, fileName: file.name };
    },
  });
}

function HostedLoginUI({ loginUrl }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <WhatsAppLogo size={28} color="#856404" />
          <Typography variant="h6" sx={{ color: '#856404' }}>
            Completa la connessione WhatsApp
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, color: '#856404' }}>
          Clicca il pulsante qui sotto per aprire la pagina di collegamento WhatsApp Business.
        </Typography>

        <Box sx={{ textAlign: 'left', mb: 3, px: 2 }}>
          <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'bold', mb: 1 }}>
            Come collegare WhatsApp Business:
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            1. Nella pagina di collegamento premi <strong>Collega WhatsApp</strong> e accedi con Facebook
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            2. Seleziona il numero di WhatsApp Business di questa segreteria
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            3. Crea il profilo aziendale, oppure scegli quello che hai già
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            4. Collega il numero aprendo l'app <strong>WhatsApp Business</strong> sul telefono quando te lo chiede
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            5. Quando ti chiede di condividere i messaggi, scegli di <strong>non condividerli</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            6. Torna sulla pagina di Meta e premi <strong>Finito</strong>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<WhatsAppLogo size={20} color="#fff" />}
            sx={{
              backgroundColor: '#25D366',
              '&:hover': { backgroundColor: '#1da851' },
              color: 'white',
              fontWeight: 'bold'
            }}
            onClick={() => window.open(loginUrl, '_blank')}
          >
            APRI PAGINA DI COLLEGAMENTO
          </Button>

          <Button
            variant="outlined"
            size="small"
            sx={{ color: '#856404', borderColor: '#856404' }}
            onClick={handleCopy}
          >
            {copied ? '✓ COPIATO!' : 'COPIA LINK'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

function PaymentRequiredUI({ paymentSettingsUrl }) {
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, backgroundColor: '#fff3cd', borderLeft: '4px solid #ff9f0a' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <WhatsAppLogo size={28} color="#856404" />
          <Typography variant="h6" sx={{ color: '#856404' }}>
            Manca il metodo di pagamento
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#856404', mb: 2 }}>
          WhatsApp è collegato, ma Meta addebita i messaggi inviati in automatico direttamente sul tuo account WhatsApp Business:
          finché non aggiungi un metodo di pagamento non possiamo inviarli.
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#ff9f0a',
            '&:hover': { backgroundColor: '#e08a00' },
            color: 'white',
            fontWeight: 'bold'
          }}
          onClick={() => window.open(paymentSettingsUrl, '_blank', 'noopener')}
        >
          AGGIUNGI METODO DI PAGAMENTO
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Lo stato si aggiorna da solo dopo che hai aggiunto il metodo. Se il link non apre la pagina giusta:
          WhatsApp Manager → Impostazioni → Impostazioni di pagamento → Aggiungi metodo di pagamento.
        </Typography>
      </Box>
    </Paper>
  );
}

function WhatsAppStatusIndicator({ phoneUuid }) {
  const { data: status, isLoading, error } = useWhatsappStatus(phoneUuid);
  const connectMutation = useConnectWhatsapp(phoneUuid);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CircularProgress size={16} sx={{ color: '#25D366' }} />
        <Typography variant="body2" color="text.secondary">
          Verificando stato WhatsApp...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          ⚠️ Impossibile verificare lo stato WhatsApp
        </Typography>
      </Alert>
    );
  }

  if (status?.connectionStatus === 'LOGGED_IN') {
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#e8f8f3', border: '1px solid #25D366' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#128C7E', fontWeight: 'bold' }}>
            WhatsApp Connesso
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Il tuo WhatsApp Business è collegato e pronto per inviare messaggi.
        </Typography>
      </Paper>
    );
  }

  if (status?.connectionStatus === 'PAYMENT_REQUIRED') {
    return <PaymentRequiredUI paymentSettingsUrl={status.paymentSettingsUrl} />;
  }

  const loginUrl = connectMutation.data?.loginUrl;
  if (loginUrl) {
    return <HostedLoginUI loginUrl={loginUrl} />;
  }

  // LOGGED_OUT - show connect button
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, backgroundColor: '#f8d7da', borderLeft: '4px solid #dc3545' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <WhatsAppLogo size={28} color="#721c24" />
          <Typography variant="h6" sx={{ color: '#721c24' }}>
            WhatsApp non collegato
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#721c24', mb: 2 }}>
          Per utilizzare questa funzionalità devi collegare il tuo account WhatsApp.
        </Typography>
        <Button
          variant="contained"
          startIcon={<WhatsAppLogo size={20} color="#fff" />}
          sx={{
            backgroundColor: '#25D366',
            '&:hover': { backgroundColor: '#1da851' },
            color: 'white',
            fontWeight: 'bold'
          }}
          onClick={() => connectMutation.mutate()}
          disabled={connectMutation.isLoading}
        >
          {connectMutation.isLoading ? 'COLLEGAMENTO IN CORSO...' : 'COLLEGA IL TUO WHATSAPP'}
        </Button>
      </Box>
    </Paper>
  );
}

function AttachmentUploader({ attachment, onAttachmentChange }) {
  const fileInputRef = React.useRef(null);
  const uploadMutation = useAttachmentUpload();
  const [error, setError] = React.useState(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setError(`Il file è troppo grande. Dimensione massima: ${MAX_ATTACHMENT_SIZE_MB}MB`);
      return;
    }

    try {
      const result = await uploadMutation.mutateAsync(file);
      onAttachmentChange({ fileId: result.fileKey, fileName: result.fileName });
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Errore durante il caricamento del file. Riprova.');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = () => {
    onAttachmentChange(undefined);
  };

  if (attachment) {
    return (
      <Paper
        elevation={1}
        sx={{
          p: 2,
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#1565c0' }}>
            📎 {attachment.fileName}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleRemoveAttachment}
          sx={{ color: '#d32f2f' }}
          title="Rimuovi allegato"
        >
          ✕
        </IconButton>
      </Paper>
    );
  }

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png,.webp,.pdf"
      />

      <Button
        variant="outlined"
        size="small"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadMutation.isLoading}
        sx={{
          color: '#666',
          borderColor: '#ccc',
          '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' }
        }}
      >
        {uploadMutation.isLoading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Caricamento...
          </>
        ) : (
          '📎 Aggiungi allegato'
        )}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Max {MAX_ATTACHMENT_SIZE_MB}MB - Immagini, video, audio, documenti
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

function SendWhatsapp() {
  const { isEditMode } = useDialplanEditorContext();
  const { node, setNodeChild } = useDialplanNodeContext();
  const { text, phoneUuid, attachment } = node;

  const handleAttachmentChange = (newAttachment) => {
    setNodeChild("attachment")(newAttachment);
  };

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item xs={12}>
          <div style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8
          }}>
            <WhatsAppLogo size={32} />
            <Typography variant="h6" align="left">
              Invia messaggio WhatsApp
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }} justifyContent="flex-start">
          <Typography variant="body" display={"inline"}>
            Invia un messaggio WhatsApp al numero chiamante con il testo personalizzato.
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <WhatsAppStatusIndicator phoneUuid={phoneUuid} />
        </Grid>

        {isEditMode && (
          <Grid container sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Messaggio da inviare:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={text || ""}
                onChange={(e) => setNodeChild("text")(e.target.value)}
                placeholder="Inserisci il messaggio WhatsApp..."
                inputProps={{ maxLength: 1000 }}
                helperText={`${(text || "").length}/1000 caratteri`}
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <AttachmentUploader
                attachment={attachment}
                onAttachmentChange={handleAttachmentChange}
              />
            </Grid>
          </Grid>
        )}

        {!isEditMode && text && (
          <Grid container sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{
                backgroundColor: '#e8f5e8',
                padding: 2,
                borderRadius: 2,
                border: '1px solid #25D366'
              }}>
                <Typography variant="body2" sx={{
                  fontStyle: 'italic',
                  color: '#1b5e20'
                }}>
                  💬 "{text}"
                </Typography>
                {attachment && (
                  <Typography variant="body2" sx={{ color: '#1565c0', mt: 1 }}>
                    📎 {attachment.fileName}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              📱 <strong>Requisiti:</strong> Il numero chiamante deve avere WhatsApp attivo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ☎️ <strong>Nota:</strong> Il messaggio viene inviato solo ai numeri di cellulare.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <DownArrow/>
      <DialplanNode prop="next"/>
    </>
  );
}

SendWhatsapp.description = "Invia un messaggio WhatsApp personalizzato al numero chiamante";
SendWhatsapp.title = "Invia messaggio WhatsApp";
SendWhatsapp.default = {
  type: "SEND_WHATSAPP",
  text: "Grazie per aver chiamato! Ti abbiamo inviato questo messaggio per confermare la tua richiesta.",
  next: {
    ...TtsAnnounce.default,
    text: "Ti abbiamo inviato un messaggio WhatsApp!",
    next: Hangup.default
  }
};
SendWhatsapp.isAdvanced = true;

export default SendWhatsapp;
